// Simple in-memory cache for API responses
class ApiCache {
  constructor() {
    this.cache = new Map();
    this.pendingRequests = new Map();
  }

  // Generate cache key from URL and params
  generateKey(url, params = {}) {
    const sortedParams = Object.keys(params)
      .sort()
      .reduce((result, key) => {
        result[key] = params[key];
        return result;
      }, {});
    
    return `${url}?${JSON.stringify(sortedParams)}`;
  }

  // Check if cache entry is still valid
  isValid(entry, maxAge = 5 * 60 * 1000) { // 5 minutes default
    return entry && (Date.now() - entry.timestamp < maxAge);
  }

  // Get cached response
  get(url, params, maxAge) {
    const key = this.generateKey(url, params);
    const entry = this.cache.get(key);
    
    if (this.isValid(entry, maxAge)) {
      return entry.data;
    }
    
    return null;
  }

  // Set cached response
  set(url, params, data) {
    const key = this.generateKey(url, params);
    this.cache.set(key, {
      data,
      timestamp: Date.now()
    });
  }

  // Get pending request promise
  getPendingRequest(url, params) {
    const key = this.generateKey(url, params);
    return this.pendingRequests.get(key);
  }

  // Set pending request promise
  setPendingRequest(url, params, promise) {
    const key = this.generateKey(url, params);
    this.pendingRequests.set(key, promise);
    
    // Clean up when promise resolves
    promise.finally(() => {
      this.pendingRequests.delete(key);
    });
  }

  // Clear cache
  clear() {
    this.cache.clear();
    this.pendingRequests.clear();
  }

  // Clear specific cache entry
  delete(url, params) {
    const key = this.generateKey(url, params);
    this.cache.delete(key);
    this.pendingRequests.delete(key);
  }

  // Clear cache entries older than specified age
  cleanup(maxAge = 10 * 60 * 1000) { // 10 minutes default
    const now = Date.now();
    for (const [key, entry] of this.cache.entries()) {
      if (now - entry.timestamp > maxAge) {
        this.cache.delete(key);
      }
    }
  }
}

// Create singleton instance
export const apiCache = new ApiCache();

// Auto cleanup every 5 minutes
setInterval(() => {
  apiCache.cleanup();
}, 5 * 60 * 1000);

export default ApiCache;